import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Table, Button, Modal, Form, InputGroup, Spinner, Badge } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const SUPER_ADMIN_VALUE = 0;

const initialFormState = {
  name: ''
};

const ManageRoles = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [editingRole, setEditingRole] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const normalizeName = useCallback((value) => {
    if (!value || typeof value !== 'string') return '';
    return value.trim();
  }, []);

  const isFetchingRef = useRef(false);

  const fetchRoles = useCallback(async ({ showLoader = true } = {}) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    if (showLoader) setLoading(true);

    try {
      const response = await api.roles.list();
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load roles.');
        return;
      }
      setRoles(Array.isArray(response.data.data) ? response.data.data : []);
    } catch (error) {
      toast.error(`Failed to load roles: ${error.response?.data?.message || error.message}`);
    } finally {
      if (showLoader) setLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const filteredRoles = useMemo(() => {
    if (!searchTerm.trim()) return roles;
    const term = searchTerm.trim().toLowerCase();
    return roles.filter(role =>
      role.name?.toLowerCase().includes(term) ||
      String(role.value).includes(term)
    );
  }, [roles, searchTerm]);

  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setFormErrors({});
    setEditingRole(null);
  }, []);

  const handleShowModal = useCallback((role = null) => {
    if (role) {
      setEditingRole(role);
      setFormState({ name: role.name || '' });
    } else {
      resetForm();
    }
    setFormErrors({});
    setShowModal(true);
  }, [resetForm]);

  const handleCloseModal = useCallback(() => {
    if (submitting) return;
    setShowModal(false);
    resetForm();
  }, [resetForm, submitting]);

  const validateForm = useCallback(() => {
    const errors = {};
    const name = normalizeName(formState.name);
    if (!name) errors.name = 'Role name is required';
    else if (name.length > 255) errors.name = 'Role name must be 255 characters or fewer';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState.name, normalizeName]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: normalizeName(formState.name)
    };
    setSubmitting(true);

    try {
      if (editingRole) {
        await api.roles.update(editingRole.id, payload);
        toast.success('Role updated successfully.');
      } else {
        await api.roles.create(payload);
        toast.success('Role created successfully.');
      }
      await fetchRoles();
      handleCloseModal();
    } catch (error) {
      toast.error(`Failed to save role: ${error.response?.data?.message || error.message}`);
    } finally {
      setSubmitting(false);
    }
  }, [editingRole, fetchRoles, formState.name, handleCloseModal, normalizeName, validateForm]);

  const handleDeleteClick = useCallback((role) => {
    setRoleToDelete(role);
    setShowDeleteModal(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!roleToDelete) return;
    setDeleteLoading(true);
    try {
      await api.roles.remove(roleToDelete.id);
      toast.success(`Role "${roleToDelete.name}" deleted successfully.`);
      await fetchRoles();
    } catch (error) {
      toast.error(`Failed to delete role: ${error.response?.data?.message || error.message}`);
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setRoleToDelete(null);
    }
  }, [fetchRoles, roleToDelete]);

  const handleDeleteCancel = useCallback(() => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
    setRoleToDelete(null);
  }, [deleteLoading]);

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      {/* Header */}
      <div className="mb-3">
        <h4 className="fw-bold mb-0 text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Manage Role
        </h4>
      </div>

      {/* Search + Add */}
      <Row className="mb-3 g-3 align-items-center">
        <Col xs={12} md={6} lg={5}>
          <InputGroup className="shadow-sm rounded-lg sm:rounded-xl overflow-hidden">
            <InputGroup.Text className="bg-white border-0 text-purple-600 px-3">
              <FaSearch className="w-5 h-5" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-0 focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </InputGroup>
        </Col>
        <Col xs={12} md={6} lg={7} className="text-end">
          <Button
            bsPrefix="custom-hamburger"
            onClick={() => handleShowModal()}
            className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 px-8 py-2"
          >
            <FaPlus className="w-5 h-5" />
            <span>Add Role</span>
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-16">
          <Spinner animation="border" className="w-12 h-12 text-purple-600" />
        </div>
      ) : filteredRoles.length === 0 ? (
        <div className="text-center py-12 bg-white/80 backdrop-blur rounded-2xl shadow-lg border border-purple-100">
          <p className="text-lg font-medium text-purple-700">
            {searchTerm ? 'No roles found matching your search.' : 'No roles added yet. Click "Add Role" to get started.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
          <Table bordered hover className="m-0 min-w-max text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3 text-left font-bold">Role Name</th>
                <th className="py-3 px-3 text-left font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoles.map((role) => (
                <tr key={role.id} className="border-b border-purple-100 hover:bg-purple-50/50 transition-all">
                  <td className="py-3 px-4 font-medium text-gray-800">
                    {role.name}
                    {role.value === SUPER_ADMIN_VALUE && (
                      <Badge bg="primary" className="ms-2 text-xs">Default</Badge>
                    )}
                  </td>
                  <td className="py-3 px-3 text-left">
                    <div className="d-flex gap-3 justify-content-left">
                      <Button
                        bsPrefix="custom-hamburger"
                        size="sm"
                        onClick={() => handleShowModal(role)}
                        className="border-purple-300 text-purple-600 hover:bg-purple-100 rounded p-1"
                      >
                        <FaEdit className="w-5 h-5" />
                      </Button>
                      {role.value !== SUPER_ADMIN_VALUE && (
                        <Button
                          bsPrefix="custom-hamburger"
                          size="sm"
                          onClick={() => handleDeleteClick(role)}
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                        >
                          <FaTrash className="w-5 h-5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered fullscreen="sm-down">
        <Form onSubmit={handleSubmit} className="p-0 m-0 space-y-6">
          <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4">
            <Modal.Title className="font-bold text-base sm:text-lg">
              {editingRole ? 'Edit Role' : 'Add New Role'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 sm:p-5 bg-gray-50">
            <Form.Group>
              <Form.Label className="font-semibold text-gray-700 text-xs sm:text-sm">
                Role Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formState.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
                maxLength={255}
                placeholder="e.g., Instructor, Reviewer"
                disabled={submitting}
                className="rounded-lg border-purple-200 focus:ring-purple-500 text-sm"
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="bg-gray-100 border-0 p-3 sm:p-4">
            <Row className="w-100 g-2">
              <Col xs={6}>
                <Button
                  bsPrefix="custom-hamburger"
                  onClick={handleCloseModal}
                  disabled={submitting}
                  className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  Cancel
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  bsPrefix="custom-hamburger"
                  type="submit"
                  disabled={submitting}
                  className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {editingRole ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      {editingRole ? 'Update' : 'Create'} Role
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered fullscreen="sm-down">
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm Delete
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            <FaTrash size={60} className="text-red-500 mx-auto" />
          </div>
          <h5 className="font-bold text-gray-800 mb-3">Delete Role</h5>
          <p className="text-gray-600">
            Are you sure you want to delete{' '}
            <strong>{roleToDelete ? roleToDelete.name : ''}</strong>?
          </p>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteCancel}
                disabled={deleteLoading}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                {deleteLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Role'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageRoles;
